import React, { useEffect, useMemo } from 'react';
import BlockDataForm from '@plone/volto/components/manage/Form/BlockDataForm';
import withObjectBrowser from '@plone/volto/components/manage/Sidebar/ObjectBrowser';
import { isInternalURL } from '@plone/volto/helpers/Url/Url';
import { usePrevious } from '@plone/volto/helpers/Utils/usePrevious';
import {
  difference,
  replaceItemOfArray,
} from '@plone/volto/helpers/Utils/Utils';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';

import { carouselSchema } from './schema';

// Helper para obter alt/title/Title de um item
const getAltFromItem = (item) => item?.alt || item?.title || item?.Title || '';

// Helper para criar novo objeto data com coluna atualizada
const buildUpdatedData = (data, index, updatedColumn) => ({
  ...data,
  columns: replaceItemOfArray(data.columns, index, updatedColumn),
});

// Calcula a primeira coluna que sofreu alteração entre o estado anterior e o atual
const getColumnsDiff = ({ columns, previousColumns, data }) => {
  const diff = difference(columns, previousColumns);
  const index = diff.findIndex(Boolean);

  if (index === -1 || index >= data.columns.length) return null;

  return {
    index,
    diffColumn: diff[index] || {},
    currentColumn: data.columns[index] || {},
  };
};

// Limpa customizações quando o href some e não há overwrite
const resetCustomizedIfHrefMissing = ({
  currentColumn,
  index,
  data,
  block,
  onChangeBlock,
}) => {
  const hrefMissing = !currentColumn.href || currentColumn.href.length === 0;

  if (!hrefMissing || currentColumn.overwrite || !currentColumn.customized) {
    return;
  }

  const newColumn = { ...currentColumn, customized: {} };
  onChangeBlock(block, buildUpdatedData(data, index, newColumn));
};

// Atualiza campos customizados a partir do href de origem (quando houver)
const handleHrefSourceChange = ({
  diffColumn,
  currentColumn,
  data,
  index,
  block,
  onChangeBlock,
}) => {
  if (
    !diffColumn?.href?.[0] ||
    currentColumn.overwrite ||
    currentColumn.withoutSource
  ) {
    return;
  }

  const hrefItem = diffColumn.href[0];
  const isExternalUrl = hrefItem?.['@id']
    ? !isInternalURL(hrefItem['@id'])
    : false;

  const altValue = getAltFromItem(hrefItem) || '';
  const titleValue = hrefItem?.title || hrefItem?.Title || '';
  const descValue = hrefItem?.Description || '';

  onChangeBlock(
    block,
    buildUpdatedData(data, index, {
      ...currentColumn,
      overwrite: isExternalUrl ? true : currentColumn.overwrite,
      customized: {
        ...currentColumn.customized,
        Title_desktop: titleValue,
        Description_desktop: descValue,
        alt_desktop: altValue,
      },
    }),
  );
};

// Helper para tratar mudanças nas imagens de preview
const handlePreviewImageChange = ({
  diffColumn,
  currentColumn,
  data,
  index,
  block,
  onChangeBlock,
}) => {
  const customizedDiff = diffColumn.customized;
  const previewKeys = [
    'preview_image_desktop',
    'preview_image_tablet',
    'preview_image_phone',
  ];

  const previewImageChanged =
    !!customizedDiff &&
    previewKeys.some((key) =>
      Object.prototype.hasOwnProperty.call(customizedDiff, key),
    );

  if (!previewImageChanged) return;

  const hasAnyPreviewImage = previewKeys.some(
    (key) => !!currentColumn?.customized?.[key],
  );

  const updatedCustomized = {
    ...currentColumn?.customized,
  };

  const deviceMap = {
    preview_image_desktop: 'alt_desktop',
    preview_image_tablet: 'alt_tablet',
    preview_image_phone: 'alt_phone',
  };

  previewKeys.forEach((key) => {
    if (Object.prototype.hasOwnProperty.call(customizedDiff, key)) {
      const val = customizedDiff[key];
      let altValue = null;

      if (typeof val === 'string') {
        altValue = null;
      } else if (Array.isArray(val)) {
        altValue = getAltFromItem(val[0]) || null;
      } else if (val && typeof val === 'object') {
        altValue = getAltFromItem(val) || null;
      }

      if (!altValue) {
        const doc = currentColumn.href?.[0];
        altValue = getAltFromItem(doc) || null;
      }

      if (altValue) {
        updatedCustomized[deviceMap[key]] = altValue;
      }
    }
  });

  onChangeBlock(
    block,
    buildUpdatedData(data, index, {
      ...currentColumn,
      customized: updatedCustomized,
      overwrite: hasAnyPreviewImage ? true : currentColumn.overwrite,
    }),
  );
};

// Ajusta overwrite quando o flag withoutSource muda
const handleWithoutSourceToggle = ({
  diffColumn,
  currentColumn,
  data,
  index,
  block,
  onChangeBlock,
}) => {
  if (!Object.prototype.hasOwnProperty.call(diffColumn, 'withoutSource')) {
    return;
  }

  const overwrite = !!diffColumn.withoutSource;

  onChangeBlock(
    block,
    buildUpdatedData(data, index, {
      ...currentColumn,
      overwrite,
    }),
  );
};

const CarouselData = (props) => {
  const {
    block,
    data,
    onChangeBlock,
    schemaEnhancer,
    blocksErrors,
    onRequestFocusIndex,
  } = props;

  const columns = useMemo(() => data.columns || [], [data.columns]);
  const previousColumns = usePrevious(columns);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!previousColumns || !columns || !Array.isArray(data.columns)) return;

    const diffResult = getColumnsDiff({ columns, previousColumns, data });
    if (!diffResult) return;

    const { index, diffColumn, currentColumn } = diffResult;

    resetCustomizedIfHrefMissing({
      currentColumn,
      index,
      data,
      block,
      onChangeBlock,
    });

    handleHrefSourceChange({
      diffColumn,
      currentColumn,
      data,
      index,
      block,
      onChangeBlock,
    });

    handlePreviewImageChange({
      diffColumn,
      currentColumn,
      data,
      index,
      block,
      onChangeBlock,
    });

    handleWithoutSourceToggle({
      diffColumn,
      currentColumn,
      data,
      index,
      block,
      onChangeBlock,
    });

    if (onRequestFocusIndex) {
      onRequestFocusIndex(index);
    }
  }, [
    columns,
    block,
    data,
    onChangeBlock,
    onRequestFocusIndex,
    previousColumns,
  ]);

  const intl = useIntl();
  const schema = schemaEnhancer
    ? schemaEnhancer(carouselSchema({ ...props, intl }), props)
    : carouselSchema({ ...props, intl });

  return (
    <BlockDataForm
      schema={schema}
      title={schema.title}
      onChangeBlock={onChangeBlock}
      onChangeField={(id, value) => {
        onChangeBlock(block, {
          ...data,
          [id]: value,
        });
      }}
      formData={data || { '@type': 'carousel', columns: [{}] }}
      block={block}
      errors={blocksErrors}
    />
  );
};

CarouselData.propTypes = {
  data: PropTypes.shape({
    columns: PropTypes.arrayOf(PropTypes.object),
  }).isRequired,
  block: PropTypes.string.isRequired,
  onChangeBlock: PropTypes.func.isRequired,
  schemaEnhancer: PropTypes.func,
  blocksErrors: PropTypes.object,
};

export default withObjectBrowser(CarouselData);
